import DiagramError from '../common/DiagramError';
import { randomId } from '../common/utils';
import { type CstNodeLocation, type CstNode } from 'chevrotain';
import type Diagram from '../common/Diagram';

type DiagramNodeId = string;
type DiagramEdgeId = string;

type DiagramProperties = {
  [key: string]: {
    value: string | number;
    keyLocation: CstNodeLocation;
    valueLocation: CstNodeLocation;
    parseNode?: CstNode;
  };
};

// A node in the diagram, typically representing a box or text to be laid out.
class DiagramNode {
  id!: DiagramNodeId;
  title!: string;
  // The label is a shorthand way to refer to the node. If there is no label, then the title is the label.
  label!: string;

  children: DiagramNodeId[] = [];
  // Edges in or out of this node.
  edges: DiagramEdgeId[] = [];

  properties: DiagramProperties = {};
  location!: CstNodeLocation;

  // Attributes computed during the layout phase. x,y represents the center of
  // the node bounding box.
  layout: any = {};

  // If this node is a sub-diagram, this is the database for that diagram.
  database?: DiagramDatabase;
  subDiagram?: Diagram;
}

// A node that contains a sub-diagram
type SubDiagramNode = DiagramNode & {
  database: DiagramDatabase;
  subDiagram: Diagram;
};

// A node that is an instance of another node.
// TODO: This needs to be rewritten to use a deep copy of the node, including
// a deep copy of any nested database. This is so that node IDs in the resulting
// diagram are unique. Right now, multiple instances of a sub-diagram only actually
// renders one instance because the whiteboard object IDs are not unique.
class InstanceDiagramNode extends DiagramNode {
  // Node this is an instance of. Proxies properties of the original node.
  instanceOf: DiagramNode;

  constructor(instanceOf: DiagramNode) {
    super();
    this.instanceOf = instanceOf;

    return new Proxy(this, {
      get(target, prop, receiver) {
        if (['database', 'properties'].includes(String(prop))) {
          return target.instanceOf[prop as keyof DiagramNode];
        } else {
          return Reflect.get(target, prop, receiver);
        }
      },
    });
  }
}

// An edge connects two nodes.
class DiagramEdge {
  id!: DiagramEdgeId;
  from!: DiagramNodeId;
  to!: DiagramNodeId;
  caption!: DiagramEdgeId;

  properties: DiagramProperties = {};
  location!: CstNodeLocation;

  // Attributes computed during the layout phase.
  layout: any = {};
}

// Represent the structure of a parse as a graph and tree simultaneously.
// The graph structure comes from the connectors between nodes. The tree
// structure comes from the nesting of nodes. Diagrams can traverse the database
// in the way that is appropriate for their layout.
//
// To make traversal easier links are stored based only on the ID of the node.
// This ensures that a node only exists once in the databse.
class DiagramDatabase {
  diagramType: string = ''; // Default diagram type.
  diagramTypeParseNode?: CstNode;

  // Properties that apply to the entire diagram.
  properties: DiagramProperties = {};
  propertiesParseNode?: CstNode[];

  // Layout information for the overall diagram.
  layout: any = {};

  // Top-level nodes in the diagram. This is the root of the tree structure.
  children: DiagramNodeId[] = [];

  // All nodes in the diagram in definition order.
  nodeIds: DiagramNodeId[] = [];

  // An iterable collection of the nodes in the diagram.
  *nodes(): Iterable<DiagramNode> {
    for (let i = 0; i < this.nodeIds.length; i++) {
      yield this.node(this.nodeIds[i]);
    }
  }

  // An iterable collection of the edges in the diagram.
  *edges(): Iterable<DiagramEdge> {
    for (const edgeId in this.edgesById) {
      yield this.edge(edgeId);
    }
  }

  // All nodes and edges, indexed by ID. This defines the graph structure.
  nodesById: { [key: DiagramNodeId]: DiagramNode } = {};
  edgesById: { [key: DiagramEdgeId]: DiagramEdge } = {};

  node(id: DiagramNodeId): DiagramNode {
    if (!this.nodesById[id]) {
      // Should never happen.
      throw new DiagramError(`Node with ID "${id}" not found.`);
    }
    return this.nodesById[id];
  }
  edge(id: DiagramEdgeId): DiagramEdge {
    if (!this.edgesById[id]) {
      // Should never happen.
      throw new DiagramError(`Edge with ID "${id}" not found.`);
    }
    return this.edgesById[id];
  }

  // Whether a label has been used. Used to ensure that labels are unique.
  labelInUse: { [key: string]: boolean } = {};

  // How we handle items with the same title. If true we only keep one instance
  // of each item. If false we keep all items.
  get nodesAreUnique(): boolean {
    if (this.diagramType === 'flowchart') return true; // TODO: This should be read from the diagram class.
    return false;
  }

  // Lookup nodes by their title.
  nodesByTitle: { [key: string]: DiagramNodeId } = {};
  findNodeByTitle(title: string): DiagramNodeId {
    return this.nodesByTitle[title];
  }

  // Lookup a note using its label.
  findNodeByLabel(label: string): DiagramNodeId | undefined {
    if (this.labelInUse[label]) {
      return this.nodesByTitle[label];
    }
  }

  // Create or lookup a node by its title.
  createNode({
    id,
    label,
    title,
    properties = {},
    location = this.nullLocation(),
    database,
    layout,
    instanceOf,
  }: {
    id?: string;
    label?: string;
    title: string;
    properties?: DiagramProperties;
    location?: CstNodeLocation;
    database?: DiagramDatabase;
    instanceOf?: DiagramNode;
    layout?: DiagramNode['layout'];
  }) {
    // Check if the same label is being defined twice.
    if (label && this.labelInUse[label]) {
      throw new DiagramError(`Label "${label}" is already in use.`, location);
    }

    if (this.nodesAreUnique && !label) {
      // See if we already have a node with this title. If we are defining a
      // new label then the node is unique by definition.
      const existingNodeId = this.findNodeByTitle(title);
      if (existingNodeId) return existingNodeId;
    }

    // Create a new node.
    let node: DiagramNode;
    if (instanceOf) {
      node = new InstanceDiagramNode(instanceOf);
    } else {
      node = new DiagramNode();
    }

    if (id && this.nodesById[id]) {
      throw new Error('Node ID already in use');
    }

    do {
      node.id = id || randomId();
    } while (this.nodesById[node.id]);
    node.title = title;
    if (label) {
      // Define a label.
      this.labelInUse[label] = true;
      this.nodesByTitle[label] = node.id;
    } else {
      this.nodesByTitle[title] = node.id;
    }
    node.properties = properties;
    node.location = location;

    node.database = database;
    if (layout) {
      node.layout = { ...node.layout, ...layout };
    }

    this.nodeIds.push(node.id);
    this.nodesById[node.id] = node;

    return node.id;
  }

  // Remove a node
  removeNode(nodeId: DiagramNodeId): DiagramNode | undefined {
    const node = this.node(nodeId);
    if (!node) return;

    if (node.label) {
      delete this.labelInUse[node.label];
      delete this.nodesByTitle[node.label];
    } else {
      delete this.nodesByTitle[node.title];
    }
    const idx = this.nodeIds.indexOf(node.id);
    if (idx > -1) {
      this.nodeIds.splice(idx, 1);
    }
    delete this.nodesById[node.id];

    return node;
  }

  // Create an edge between two nodes.
  createEdge(
    fromNodeId: DiagramNodeId,
    toNodeId: DiagramNodeId,
    properties: DiagramProperties,
    location: CstNodeLocation
  ): DiagramEdgeId {
    const edge = new DiagramEdge();

    do {
      edge.id = randomId();
    } while (this.edgesById[edge.id]);
    edge.from = fromNodeId;
    edge.to = toNodeId;
    edge.properties = properties;
    edge.location = location;

    if (edge.properties.caption) {
      edge.caption = edge.properties.caption.value as string;
      delete edge.properties.caption;
    }

    this.edgesById[edge.id] = edge;
    this.node(fromNodeId).edges.push(edge.id);
    this.node(toNodeId).edges.push(edge.id);

    return edge.id;
  }

  // Build the hierarchy of children based on indentation level of the nodes.
  buildHierarchy() {
    const nodesToProcess = this.nodeIds.slice();
    const recurseHierarchy = (children: DiagramNodeId[], column: number) => {
      let nodeId: DiagramNodeId | void;
      while ((nodeId = nodesToProcess.shift())) {
        const node = this.node(nodeId);
        const nodeColumn = node.location.startColumn!;

        if (nodeColumn === column) {
          children.push(nodeId);
        } else if (nodeColumn > column) {
          const lastChildId = children[children.length - 1];
          const lastChildren = this.node(lastChildId).children;
          lastChildren.push(nodeId);
          recurseHierarchy(lastChildren, nodeColumn);
        } else if (nodeColumn < column) {
          // This is tricky, we need to return from recursion to the correct level.
          nodesToProcess.unshift(nodeId);
          return;
        }
      }
    };

    let nodeId: DiagramNodeId | void;
    while ((nodeId = nodesToProcess.shift())) {
      this.children.push(nodeId);
      const node = this.node(nodeId);

      recurseHierarchy(this.children, node.location.startColumn!);
    }
  }

  setDiagramType(diagramType: string) {
    this.diagramType = diagramType;
    if (this.diagramTypeParseNode) {
      // @ts-expect-error - Property 'DiagramType' does not exist on type 'CstNode'
      this.diagramTypeParseNode.DiagramType[0].image = diagramType;
    }
  }

  setProperty(propertyName: string, propertyValue: string | number) {
    if (
      this.properties[propertyName] &&
      this.properties[propertyName].parseNode
    ) {
      // Update value of existing property.
      // @ts-expect-error - Property 'ValueText' does not exist on type 'CstNode'
      this.properties[propertyName].parseNode.ValueText[0].image =
        propertyValue;
    } else if (this.propertiesParseNode) {
      let needsComma = '';
      if (this.propertiesParseNode[0].children.property) {
        needsComma = ',';
      } else {
        needsComma = ' ';
      }
      // Add a property to the existing properties list.
      // @ts-expect-error - Property 'trailingWhitespace' does not exist on type 'CstNode'.
      this.propertiesParseNode[0].children.LCurly[0].trailingWhitespace = {
        image: ` ${propertyName}: ${propertyValue}${needsComma}`,
      };
    } else if (this.diagramTypeParseNode) {
      // Add a property to the diagram type node.
      // @ts-expect-error - Property 'DiagramType' does not exist on type 'CstNode'
      this.diagramTypeParseNode.DiagramType[0].trailingWhitespace = {
        image: ` { ${propertyName}: ${propertyValue} }`,
      };
    } else {
      console.log(
        `Unable to find an attachment point for new property '${propertyName}'`
      );
    }
  }

  nullLocation(): CstNodeLocation {
    return {
      startOffset: 0,
      endOffset: 0,
      startLine: 0,
      endLine: 0,
      startColumn: 0,
      endColumn: 0,
    };
  }

  // Convert the database back to a string. This does not preserve whitespace
  // or comments.
  toString(): string {
    let output = `${this.diagramType}${this.propertiesToString(
      this.properties
    )}\n\n`;
    const edgesProcessed: Set<string> = new Set();
    const nodesProcessed: Set<string> = new Set();

    for (const nodeId of this.children) {
      if (nodesProcessed.has(nodeId)) continue;

      output += this.stepToString(
        this.node(nodeId),
        0,
        nodesProcessed,
        edgesProcessed
      );
    }

    return output;
  }

  stepToString(
    node: DiagramNode,
    indent: number,
    nodesProcessed: Set<string>,
    edgesProcessed: Set<string>
  ): string {
    let output = '';
    output += '  '.repeat(indent);
    output += this.nodeToString(node);

    // TODO: This doesn't handle labels properly
    if (node.edges.length > 0) {
      console.log('node.edges', node.edges);
      let repeatNode = false;
      for (const edgeId of node.edges) {
        if (edgesProcessed.has(edgeId)) continue;
        edgesProcessed.add(edgeId);
        nodesProcessed.add(node.id);

        const edge = this.edge(edgeId);
        if (edge.from !== node.id) continue;

        if (repeatNode) {
          output += '\n';
          output += this.nodeToString(node);
        }
        repeatNode = true;

        output += this.edgeToString(edge);

        const destNode = this.node(edge.to);
        output += this.nodeToString(destNode);
      }
    }
    output += '\n';

    if (node.children.length > 0) {
      for (const childId of node.children) {
        if (nodesProcessed.has(childId)) continue;

        output += this.stepToString(
          this.node(childId),
          indent + 1,
          nodesProcessed,
          edgesProcessed
        );
      }
      output += '  '.repeat(indent);
    }

    return output;
  }

  nodeToString(node: DiagramNode): string {
    let output = '';

    // The first time we see a node define a label. Otherwise use the label.
    // TODO:

    if (node.label) {
      output += `${node.label}: `;
    }
    output += node.title;
    output += this.propertiesToString(node.properties);

    return output;
  }

  edgeToString(edge: DiagramEdge): string {
    let output = ' ';
    const edgeProps = structuredClone(edge.properties);
    const leftProp = (edgeProps.connectorFromStyle?.value as string) || 'none';
    const rightProp =
      (edgeProps.connectorToStyle?.value as string) || 'pointyArrow';

    if (['none', 'pointyArrow'].includes(leftProp))
      delete edgeProps.connectorFromStyle;
    if (['none', 'pointyArrow'].includes(rightProp))
      delete edgeProps.connectorToStyle;

    if (edge.caption || Object.keys(edgeProps).length > 0) {
      if (leftProp === 'none') {
        output += '---';
      } else if (leftProp === 'pointyArrow') {
        output += '<--';
      }

      if (edge.caption) {
        output += ` ${edge.caption} `;
      }

      output += this.propertiesToString(edgeProps);

      if (rightProp === 'none') {
        output += '---';
      } else if (rightProp === 'pointyArrow') {
        output += '-->';
      }
    } else {
      if (leftProp === 'none' && rightProp === 'none') {
        output += '---';
      } else if (leftProp === 'none' && rightProp === 'pointyArrow') {
        output += '->';
      } else if (leftProp === 'pointyArrow' && rightProp === 'none') {
        output += '<-';
      } else if (leftProp === 'pointyArrow' && rightProp === 'pointyArrow') {
        output += '<->';
      }
    }

    return output + ' ';
  }

  propertiesToString(properties: DiagramProperties): string {
    const props: string[] = [];
    for (const key in properties) {
      const property = properties[key];
      props.push(`${key}: ${property.value}`);
    }

    if (props.length === 0) {
      return '';
    } else if (props.length === 1) {
      return ' { ' + props[0] + ' } ';
    } else {
      return ' {\n    ' + props.join(',\n    ') + '\n  } ';
    }
  }
}

export {
  DiagramDatabase,
  DiagramNode,
  DiagramEdge,
  type SubDiagramNode,
  type DiagramProperties,
  type DiagramNodeId,
  type DiagramEdgeId,
};
